export type ToastType = "success" | "warning" | "danger" | "info";

export interface ToastOptions {
  type: ToastType;
  title: string;
  message?: string;
  durationMs?: number;
}

// Ícones SVG inline para cada tipo de toast
const TOAST_ICONS: Record<ToastType, string> = {
  success: `<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  warning: `<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 1.5L11 10.5H1L6 1.5Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M6 5V7.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="6" cy="9" r="0.5" fill="currentColor"/>
  </svg>`,

  danger: `<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,

  info: `<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.2"/>
    <path d="M6 5.5V8.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="6" cy="3.5" r="0.5" fill="currentColor"/>
  </svg>`,
};

const CLOSE_ICON = `<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
</svg>`;

class ToastService {
  private container: HTMLElement | null = null;

  private ensureContainer(): HTMLElement {
    if (!this.container) {
      let el = document.getElementById("toast-root");
      if (!el) {
        el = document.createElement("div");
        el.id = "toast-root";
        document.body.appendChild(el);
      }
      this.container = el;
    }
    return this.container;
  }

  show(options: ToastOptions) {
    const root = this.ensureContainer();
    const toast = document.createElement("div");
    toast.className = `toast toast-${options.type}`;
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");

    toast.innerHTML = `
      <span class="toast-icon" aria-hidden="true">
        ${TOAST_ICONS[options.type]}
      </span>
      <div class="toast-body">
        <div class="toast-title">${options.title}</div>
        ${options.message
          ? `<div class="toast-message">${options.message}</div>`
          : ""}
      </div>
      <button class="toast-close" aria-label="Fechar notificação" title="Fechar">
        ${CLOSE_ICON}
      </button>
    `;

    root.appendChild(toast);

    // Fechar ao clicar no X
    toast.querySelector(".toast-close")?.addEventListener("click", () => {
      this.dismiss(toast);
    });

    // Auto-dismiss
    const duration = options.durationMs ?? 4500;
    const timer = setTimeout(() => this.dismiss(toast), duration);

    // Pausar auto-dismiss ao passar o mouse
    toast.addEventListener("mouseenter", () => clearTimeout(timer));
    toast.addEventListener("mouseleave",  () => {
      setTimeout(() => this.dismiss(toast), 1500);
    });
  }

  private dismiss(toast: HTMLElement) {
    if (!toast.isConnected) return;
    toast.style.opacity = "0";
    toast.style.transform = "translateX(20px)";
    toast.style.transition = "opacity 200ms ease, transform 200ms ease";
    setTimeout(() => toast.remove(), 200);
  }

  success(title: string, message?: string) {
    this.show(message === undefined ? { type: "success", title } : { type: "success", title, message });
  }

  warning(title: string, message?: string) {
    this.show(message === undefined ? { type: "warning", title } : { type: "warning", title, message });
  }

  error(title: string, message?: string) {
    this.show(message === undefined ? { type: "danger", title } : { type: "danger", title, message });
  }

  info(title: string, message?: string) {
    this.show(message === undefined ? { type: "info", title } : { type: "info", title, message });
  }
}

export const toastService = new ToastService();
